<template>
  
  <div class="form-container">
    <div class="mb-2">
      <button
        class="btn btn-sm"
        :class="lang === 'ja' ? 'btn-primary' : 'btn-outline-primary'"
        @click="lang = 'ja'"
      >
        日本語
      </button>
      <button
        class="btn btn-sm mx-2"
        :class="lang === 'en' ? 'btn-primary' : 'btn-outline-primary'"
        @click="lang = 'en'"
      >
        English
      </button>
      
      <button
        class="btn btn-sm"
        :class="deliveryNote ? 'btn-primary' : 'btn-outline-primary'"
        @click="deliveryNote = !deliveryNote"
      >
        <span></span>納品書 {{ deliveryNote ? 'あり' : 'なし' }}
      </button>
      
      <button
        class="btn btn-sm mx-2"
        :class="sealFlg ? 'btn-primary' : 'btn-outline-primary'"
        @click="sealFlg = !sealFlg"
      >
        <span></span>押印 {{ sealFlg ? 'あり' : 'なし' }}
      </button>
    </div>

    <BillingCreateJa
      v-if="lang === 'ja'"
      :customer-monthly-billing-data="customerMonthlyBillingData"
      :buildings="buildings"
      :customers="customers"
      :annual-work-billings="annualWorkBillings"
      :temporary-work-billings="temporaryWorkBillings"
      :deliver-note="deliveryNote"
      :seal-flg="sealFlg"
      :is-submitting="isSubmitting"
      @add-row="addBillingRow"
      @remove-row="removeBillingRow"
      @update-totals="updateTotals"
      @go-back="goBack"
      @submit="requestCreateMonthlyBilling"
    />

    <BillingCreateEn
      v-if="lang === 'en'"
      :customer-monthly-billing-data="customerMonthlyBillingData"
      :buildings="buildings"
      :customers="customers"
      :grouped-billings="groupedBillings"
      :annual-work-billings="annualWorkBillings"
      :temporary-work-billings="temporaryWorkBillings"
      :deliver-note="deliveryNote"
      :seal-flg="sealFlg"
      :is-submitting="isSubmitting"
      @add-row="addBillingRow"
      @remove-row="removeBillingRow"
      @update-totals="updateTotals"
      @go-back="goBack"
      @submit="requestCreateMonthlyBilling"
    />

    <AnnualWorkCancellationConfirmModal
      :show="showCancellationConfirm"
      :cancellations="annualWorkCancellations"
      :is-submitting="isSubmitting"
      mode="create"
      @close="showCancellationConfirm = false"
      @confirm="createMonthlyBilling"
    />
  </div>
</template>

<script>
import { calculateBillingAmounts } from '@/utils/billingAmounts';
import { ref, computed, onMounted, watch, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import {
  getSelectBoxBuildings,
  getSelectBoxCustomers,
  // getBillingCustomerRepresentative,
  getCustomer,
  getNewMonthlyBillings,
  getCustomerUnconfirmedAmount,
  createMonthlyBillings
} from '@/api/axios';
import { sortAndNormalizeMonthlyBillingDetails } from '@/utils/monthlyBillingDetails';
import BillingCreateJa from './BillingCreateJa.vue';
import BillingCreateEn from './BillingCreateEn.vue';
import AnnualWorkCancellationConfirmModal from '@/components/invoices/AnnualWorkCancellationConfirmModal.vue';

export default {
  components: {
    BillingCreateJa,
    BillingCreateEn,
    AnnualWorkCancellationConfirmModal
  },
  setup() {
    const title = ref('請求書新規作成');

    watchEffect(() => {
      document.title = title.value;
    });

    const userId = ref('');
    const userName = ref('');
    const lang = ref('ja');
    const deliveryNote = ref(false);
    const sealFlg = ref(true);
    const customer = ref({});
    const buildings = ref([]);
    const customers = ref([]);
    const annualWorkBillings = ref([]);
    const temporaryWorkBillings = ref([]);
    const showCancellationConfirm = ref(false);
    const isSubmitting = ref(false);
    const errorMessages = ref({});

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const year = route.params.year || new Date().getFullYear();
    const month = route.params.month || new Date().getMonth() + 1;
    const annualWorkIds = route.query.annual_work_ids;
    const temporalyWorkIds = route.query.temporaly_work_ids;
    const today = new Date().getDate();

    const getNonBillingListQuery = () => {
      const query = { ...route.query };
      delete query.annual_work_ids;
      delete query.temporaly_work_ids;

      return {
        ...query,
        year: query.year || year,
        month: query.month || month
      };
    };

    const customerMonthlyBillingData = ref({
      m_customer_id: '',
      m_customer_representative_id: '',
      dear_nm: '',
      parent_billing_no: '',
      billing_year: '',
      billing_month: '',
      billing_dt: new Date().toISOString().slice(0, 10),
      billing_dt_display_flg: true,
      representative_nm_display_flg: '',
      total_amount: '',
      billing_total_amount: '',
      tax_rate: 10,
      bank_display_flg_01: false,
      bank_nm_01: '',
      branch_nm_01: '',
      account_type_01: '',
      account_no_01: '',
      bank_display_flg_02: false,
      bank_nm_02: '',
      branch_nm_02: '',
      account_type_02: '',
      account_no_02: '',
      bank_display_flg_03: false,
      bank_nm_03: '',
      branch_nm_03: '',
      account_type_03: '',
      account_no_03: '',
      carryover_amount: '',
      status: 1,
      billing_count: '',
      billing_lang_type: 0, // 0: 日本語, 1: 英語
      delivery_note_flg: false, // false: 納品書なし、true: 納品書あり
      billing_customer_address_display_flg: false, // 顧客住所表示フラグ
      honorific_title: '御中', // 敬称
      seal_flg: true, // 押印フラグ
      t_customer_monthly_billing_details: []
    });

    const annualMapped = ref([]);
    const tempMapped = ref([]);

    const setBankFieldsFromBanks = (banks) => {
      const slots = [
        {
          flag: 'bank_display_flg_01',
          bank_nm: 'bank_nm_01',
          branch_nm: 'branch_nm_01',
          account_type: 'account_type_01',
          account_no: 'account_no_01'
        },
        {
          flag: 'bank_display_flg_02',
          bank_nm: 'bank_nm_02',
          branch_nm: 'branch_nm_02',
          account_type: 'account_type_02',
          account_no: 'account_no_02'
        },
        {
          flag: 'bank_display_flg_03',
          bank_nm: 'bank_nm_03',
          branch_nm: 'branch_nm_03',
          account_type: 'account_type_03',
          account_no: 'account_no_03'
        }
      ];

      slots.forEach((slot, index) => {
        const bank = banks[index];
        if (bank) {
          customerMonthlyBillingData.value[slot.flag] = true;
          customerMonthlyBillingData.value[slot.bank_nm] = bank.bank_nm || '';
          customerMonthlyBillingData.value[slot.branch_nm] = bank.branch_nm || '';
          customerMonthlyBillingData.value[slot.account_type] = bank.account_type || '';
          customerMonthlyBillingData.value[slot.account_no] = bank.account_no || '';
        } else {
          customerMonthlyBillingData.value[slot.flag] = false;
          customerMonthlyBillingData.value[slot.bank_nm] = '';
          customerMonthlyBillingData.value[slot.branch_nm] = '';
          customerMonthlyBillingData.value[slot.account_type] = '';
          customerMonthlyBillingData.value[slot.account_no] = '';
        }
      });
    };

    const applyCustomerBanks = async (customerId) => {
      if (!customerId) return;
      try {
        const response = await getCustomer(customerId);
        const banks = response.data?.m_banks || [];
        setBankFieldsFromBanks(banks);
      } catch (error) {
        console.error('銀行情報の取得に失敗しました:', error);
      }
    };

    const englishOptionMapped = [
      {
        is_english_option: true,
        billing_no: null,
        m_building_id: null,
        building_nm: null,
        work_type: 2,
        target_work_id: null,
        branch_billing_no: '',
        work_year: '',
        work_month: '',
        work_dt: '',
        work_content: 'Our Ref.No,',
        breakdown: '',
        bill_amount: '',
        sort_no: null
      },
      {
        is_english_option: true,
        billing_no: null,
        m_building_id: null,
        building_nm: null,
        work_type: 2,
        target_work_id: null,
        branch_billing_no: '',
        work_year: '',
        work_month: '',
        work_dt: '',
        work_content: 'Completion of the work',
        breakdown: '',
        bill_amount: '',
        sort_no: null
      }
    ];

    const buildBillingDetails = () => {
      const manualRows = (customerMonthlyBillingData.value.t_customer_monthly_billing_details || [])
        .filter((detail) => detail.work_type === 2 && !detail.is_english_option);

      // 英語行の sort_no を毎回再計算
      englishOptionMapped[0].sort_no = annualMapped.value.length + tempMapped.value.length + manualRows.length + 1;
      englishOptionMapped[1].sort_no = annualMapped.value.length + tempMapped.value.length + manualRows.length + 2;

      return [
        ...annualMapped.value,
        ...tempMapped.value,
        ...manualRows,
        ...(lang.value === 'en' ? englishOptionMapped : [])
      ];
    };

    const applyBillingDetailOrdering = () => {
      customerMonthlyBillingData.value.t_customer_monthly_billing_details = sortAndNormalizeMonthlyBillingDetails(
        customerMonthlyBillingData.value.t_customer_monthly_billing_details
      );
    };

    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');

      try {
        const today = new Date().getDate().toString().padStart(2, '0');

        const [
          buildingsRes,
          customerRes,
          billingDataRes
        ] = await Promise.all([
          getSelectBoxBuildings(),
          getSelectBoxCustomers(),
          getNewMonthlyBillings(
            year,
            month,
            annualWorkIds,
            temporalyWorkIds
          )
        ]);

        // --- 建物情報 ---
        buildings.value = buildingsRes.data;

        // --- 請求先顧客 ---
        customers.value = customerRes.data;

        // --- 請求データ ---
        const response = billingDataRes;
        annualWorkBillings.value = response.data.annual_work_billing_details;
        temporaryWorkBillings.value = response.data.temporary_work_billing_details;

        customerMonthlyBillingData.value.dear_nm = annualWorkBillings.value[0]?.t_annual_work_monthly_billing?.m_customer?.customer_nm;
        customerMonthlyBillingData.value.parent_billing_no = `B${year.slice(-2)}${month}${today}${String(response.data?.max_billing_count).padStart(4, '0')}`;
        customerMonthlyBillingData.value.billing_count = response.data.max_billing_count;
        if (Array.isArray(temporaryWorkBillings.value) && temporaryWorkBillings.value.length > 0) {
          // customerMonthlyBillingData.value.m_customer_representative_id = temporaryWorkBillings.value[0].m_customer_representative_id;

          // 顧客情報を取得して、請求先と住所を設定
          customer.value = temporaryWorkBillings.value[0]?.m_customer;
          customerMonthlyBillingData.value.m_customer_id = customer.value.id
          customerMonthlyBillingData.value.customer_address = format_address(customer.value) || '';

          await applyCustomerBanks(customerMonthlyBillingData.value.m_customer_id);

          // 請求日を顧客マスタで設定されている締日から計算
          const billingDeadline = customer.value.billing_deadline;
          const billingDate = calculateBillingDate(year, month, billingDeadline);
          customerMonthlyBillingData.value.billing_dt = billingDate ? billingDate.toISOString().slice(0, 10) : null;
        } else if (Array.isArray(annualWorkBillings.value) && annualWorkBillings.value.length > 0) {
          try {
            // const response = await getBillingCustomerRepresentative(annualWorkBillings.value[0]?.m_building?.m_customer?.id);

            // ここで m_customer_representative_id に代入
            // customerMonthlyBillingData.value.m_customer_representative_id = response.data?.id;

            // 顧客情報を取得して、請求先と住所を設定
            customer.value = annualWorkBillings.value[0]?.m_building?.m_customer || {};
            customerMonthlyBillingData.value.m_customer_id = customer.value.id
            customerMonthlyBillingData.value.customer_address = format_address(customer.value) || '';

            await applyCustomerBanks(customerMonthlyBillingData.value.m_customer_id);

            // 請求日を顧客マスタで設定されている締日から計算
            const billingDeadline = customer.value?.billing_deadline;
            const billingDate = calculateBillingDate(year, month, billingDeadline);
            customerMonthlyBillingData.value.billing_dt = billingDate ? billingDate.toISOString().slice(0, 10) : null;
          } catch (error) {
            console.error('顧客担当者情報の取得に失敗しました:', error);
          }
        }

        // 年間作業（空配列 fallback + optional chaining）
        annualMapped.value = (annualWorkBillings.value || []).map((annual_work, index) => {
          const annualSchedule = annual_work?.t_building_work_annual_schedule;

          return {
            client_row_key: `annual-${index}-${annual_work?.id || 'none'}`,
            m_building_id: annual_work?.m_building_id || null,
            building_nm: annual_work?.m_building?.building_nm || null,
            work_type: 0,
            target_work_id: annual_work?.id || null,
            fixed_fee_summary: Boolean(annual_work?.fixed_fee_summary),
            fixed_fee_billing: Boolean(
              annual_work?.fixed_fee_billing || annual_work?.m_building?.fixed_fee_billing
            ),
            annual_work_cancellation_allowed: Boolean(
              annualSchedule && annual_work?.id && !annual_work?.fixed_fee_summary
            ),
            branch_billing_no: '',
            work_year: annual_work?.work_year || '',
            work_month: annual_work?.work_month || '',
            work_dt:
              annual_work?.t_vendor_work_monthly_calendar_detail?.work_plan_dt ||
              annual_work?.t_annual_work_monthly_calendar_detail?.work_plan_dt ||
              '',

            work_content:
              annualSchedule?.monthly_work_flg === true
                ? `${annual_work?.work_month}月度 ${annualSchedule?.work_content || ''}`
                : annualSchedule?.work_content || annual_work?.work_content || '',

            breakdown: annual_work?.breakdown || '',
            bill_amount: annual_work?.bill_amount || '',
            sort_no: index + 1
          };
        });

        console.log('annualWorkBillings:', annualWorkBillings.value);

        // 臨時作業（空配列 fallback + optional chaining）
        tempMapped.value = (temporaryWorkBillings.value || []).map((temporary_work, index) => ({
          billing_no: temporary_work?.billing_no || null,
          m_building_id: temporary_work?.m_building?.id || null,
          building_nm: temporary_work?.m_building?.building_nm || null,
          work_type: 1,
          target_work_id: temporary_work?.id || null,
          branch_billing_no: '',
          work_year: temporary_work?.work_year || '',
          work_month: temporary_work?.work_month || '',
          work_dt: temporary_work?.work_dt || '',
          work_content: temporary_work?.work_content || '',
          breakdown: temporary_work?.breakdown || '',
          bill_amount: temporary_work?.order_amount || '',
          sort_no: annualMapped.value.length + index + 1
        }));

        // 合体して代入
        customerMonthlyBillingData.value.t_customer_monthly_billing_details = buildBillingDetails();
        applyBillingDetailOrdering();

        // work_dt を監視し、年と月を設定
        customerMonthlyBillingData.value.t_customer_monthly_billing_details.forEach((billing) => {
          watchDateField(() => billing.work_dt, billing, 'work');
        });

        // billing_dt を監視し、年と月を設定
        watchDateField(
          () => customerMonthlyBillingData.value.billing_dt,
          customerMonthlyBillingData.value,
          'billing'
        );

      } catch (error) {
        console.error('請求書情報またはマスタ情報の取得に失敗しました:', error);
      }
    };

    // 言語変更を監視して billing_lang_type を更新
    watch(lang, (newLang) => {
      customerMonthlyBillingData.value.billing_lang_type = newLang === 'en' ? 1 : 0;

      // 英語行の有無を切替
      customerMonthlyBillingData.value.t_customer_monthly_billing_details = buildBillingDetails();
      applyBillingDetailOrdering();
    });

    // 変更を監視して delivery_note を更新
    watch(deliveryNote, (newVal) => {
      customerMonthlyBillingData.value.delivery_note_flg = newVal ? true : false;
    });

    // 変更を監視して seal_flg を更新
    watch(sealFlg, (newVal) => {
      customerMonthlyBillingData.value.seal_flg = newVal ? true : false;
    });

    // グループ情報を計算する：どの建物IDが何行あるか、どのindexで出てくるかをまとめる
    const groupedBillings = computed(() => {
      const grouped = {};

      // 請求詳細を1件ずつ処理
      customerMonthlyBillingData.value.t_customer_monthly_billing_details.forEach((billing, index) => {
        // work_type==2（追加行）は無視
        if (billing.work_type === 2) return;

        const id = billing.m_building_id;
        
        // 初めて見た建物IDなら初期化
        if (!grouped[id]) {
          grouped[id] = {
            count: 0,       // 同じ建物IDの出現数
            indexes: []     // 各出現位置（index）
          };
        }

        // 数を1つ増やし、indexを記録
        grouped[id].count += 1;
        grouped[id].indexes.push(index);
      });

      return grouped;
    });

    // 請求項目行追加
    const addBillingRow = () => {
      customerMonthlyBillingData.value.t_customer_monthly_billing_details.push({
        t_customer_monthly_billing_id: '',
        m_building_id: '',
        work_type: 2,
        target_work_id: '',
        branch_billing_no: '',
        work_year: '',
        work_month: '',
        work_dt: '',
        work_content: '',
        breakdown: '',
        bill_amount: '',
        sort_no: customerMonthlyBillingData.value.t_customer_monthly_billing_details.length + 1
      });
      applyBillingDetailOrdering();
    };

    // 手入力の請求項目行削除
    const removeBillingRow = (index) => {
      const detail = customerMonthlyBillingData.value.t_customer_monthly_billing_details[index];
      if (detail?.work_type !== 2) return;

      customerMonthlyBillingData.value.t_customer_monthly_billing_details.splice(index, 1);
      applyBillingDetailOrdering();
    };

    const annualWorkCancellations = computed(() => (
      customerMonthlyBillingData.value.t_customer_monthly_billing_details || []
    ).filter((detail) => (
      detail.work_type === 0 &&
      detail.annual_work_cancellation_allowed &&
      detail.cancel_annual_work
    )).map((detail) => ({
      client_row_key: detail.client_row_key,
      t_building_work_schedule_detail_id: detail.target_work_id,
      target_work_year: Number(detail.work_year) || Number(year),
      target_work_month: Number(detail.work_month) || Number(month),
      building_nm: detail.building_nm || '',
      work_content: detail.work_content || '',
      bill_amount: Number(detail.bill_amount) || 0,
      fixed_fee_billing: Boolean(detail.fixed_fee_billing),
      reason: '請求書作成時に作業未実施を確認'
    })));

    // 顧客情報から住所をフォーマットする関数（英語）
    const format_address = (customer) => {
      if (!customer) return '';
      const line1 = [customer.other_address, customer.city].filter(Boolean).join(', ');
      const line2 = [customer.prefecture, customer.postal_code].filter(Boolean).join(', ');
      return `${line1}\n${line2}`;
    };

    // 請求締日から日付を求める関数
    function calculateBillingDate(year, month, billingDeadline) {

      const deadlineDays = [5, 10, 15, 20, 25];
      
      if (billingDeadline >= 0 && billingDeadline <= 4 && billingDeadline != null) {
        return new Date(year, month - 1, deadlineDays[billingDeadline] + 1);
      } else {
        // 月末を取得：翌月1日の前日
        return new Date(year, month, 0);
      }
    }

    // 日付を監視して、変更があったら年と月を自動で詰める（空なら空にする）
    function watchDateField(source, target, prefix = '') {
      watch(
        () => typeof source === 'function' ? source() : source,
        (newVal) => {
          if (!newVal) {
            // 年間予定作業の対象年月は、実作業日が未入力でも保持する
            if (prefix === 'work' && Number(target.work_type) === 0) return;

            // ✅ newVal が null/''/undefined の場合は year, month を空に
            target[`${prefix}_year`] = '';
            target[`${prefix}_month`] = '';
            return;
          }

          const date = new Date(newVal);
          if (!isNaN(date.getTime())) {
            target[`${prefix}_year`] = date.getFullYear();
            target[`${prefix}_month`] = date.getMonth() + 1;
          } else {
            // ✅ 万が一フォーマットがおかしい場合も空にする保険
            target[`${prefix}_year`] = '';
            target[`${prefix}_month`] = '';
          }
        },
        { immediate: true }
      );
    }

    // 金額変更を監視して金額を更新
    watch(
      () => [
        customerMonthlyBillingData.value.t_customer_monthly_billing_details,
        customerMonthlyBillingData.value.carryover_amount,
        customerMonthlyBillingData.value.tax_rate
      ],
      ([newDetails]) => {
        // t_customer_monthly_billing_details の金額合計を計算
        const billing_detail_total = newDetails.reduce((sum, detail) => {
          if (detail.cancel_annual_work) return sum;
          return sum + (parseFloat(detail.bill_amount) || 0);
        }, 0);

        // 合計金額を更新
        customerMonthlyBillingData.value.total_amount = billing_detail_total;

        // 最終合計金額を更新
        updateTotals();
      },
      { deep: true } // 配列の内部要素の変更も追跡
    );

    // 請求先変更後に自動的に繰越金額を取得して、再計算させる
    watch(
      () => [customerMonthlyBillingData.value.m_customer_representative_id],
      () => {
        (async () => {
          try {
            const response = await getCustomerUnconfirmedAmount({
              customer_representative_id: customerMonthlyBillingData.value.m_customer_representative_id
            });

            // ここで carryover_amount に代入
            customerMonthlyBillingData.value.carryover_amount = response.data;
          } catch (error) {
            console.error('金額の取得に失敗しました:', error);
          }
        })();

        // 最終合計金額を更新
        updateTotals();
      },
      { deep: true }
    );

    watch(
      () => customerMonthlyBillingData.value.m_customer_id,
      (newId, oldId) => {
        if (!newId || newId === oldId) return;
        applyCustomerBanks(newId);
      }
    );

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    const calculateTax = () => calculateBillingAmounts(customerMonthlyBillingData.value).tax_amount;

    // 合計金額の計算（繰越金額を除く）
    const calculateNoCarryoverTotal = () => calculateBillingAmounts(customerMonthlyBillingData.value).current_billing_amount;

    // 合計金額の計算（繰越金額を含む）
    const calculateGrandTotal = () => {
      const grandTotal = calculateBillingAmounts(customerMonthlyBillingData.value).billing_total_amount;
      customerMonthlyBillingData.value.billing_total_amount = grandTotal;
      return grandTotal;
    };

    const updateTotals = () => {
      calculateGrandTotal();
    };

    const requestCreateMonthlyBilling = () => {
      if (isSubmitting.value) return;
      if (annualWorkCancellations.value.length > 0) {
        showCancellationConfirm.value = true;
        return;
      }

      createMonthlyBilling();
    };

    const createMonthlyBilling = async () => {
      if (isSubmitting.value) return;

      isSubmitting.value = true;
      try {
        applyBillingDetailOrdering();

        const requestData = {
          ...customerMonthlyBillingData.value,
          annual_work_cancellations: annualWorkCancellations.value,
          t_customer_monthly_billing_details: customerMonthlyBillingData.value.t_customer_monthly_billing_details
            .filter((detail) => !detail.cancel_annual_work)
        };

        // APIにデータを送信
        // customerMonthlyBillingData.value.m_customer_id = customers.value.find(customerRepresentative => 
        //   customerRepresentative.id == customerMonthlyBillingData.value.m_customer_representative_id
        // )?.m_customer_id || null;

        const response = await createMonthlyBillings(requestData);
        const billingId = response.data.billing_id;
        store.dispatch('setFlashMessage', { message: '請求書作成完了！', type: 'success' });
        router.push({
          path: `/monthly-billing-detail/${billingId}/${year}/${month}`,
          query: { year, month }
        });
      } catch (error) {
        console.error('作成中にエラーが発生しました:', error);
        const message = error.response?.data?.error || '請求書の作成に失敗しました。入力内容を確認してください。';
        store.dispatch('setFlashMessage', { message, type: 'danger' });
      } finally {
        isSubmitting.value = false;
        showCancellationConfirm.value = false;
      }
    };

    const goBack = () => {
      router.push({
        path: '/monthly-non-billings',
        query: getNonBillingListQuery(),
      });
    };

    onMounted(() => {
      fetchWorkScheduleDatas();
    });

    return {
      title,
      year,
      month,
      today,
      lang,
      deliveryNote,
      sealFlg,
      customer,
      buildings,
      customers,
      annualWorkBillings,
      temporaryWorkBillings,
      annualWorkCancellations,
      showCancellationConfirm,
      isSubmitting,
      groupedBillings,
      addBillingRow,
      removeBillingRow,
      formatCurrency,
      calculateTax,
      calculateNoCarryoverTotal,
      calculateGrandTotal,
      updateTotals,
      errorMessages,
      customerMonthlyBillingData,
      requestCreateMonthlyBilling,
      createMonthlyBilling,
      goBack
    };
  }
};
</script>

<style>
.form-container {
  width: 90% !important;
}

</style>
